import { language } from '../i18n';

/**
 * Which page of the user guide explains which screen. Pages are under the
 * guide's address (DOCS_URL), as published by apps/docs (npm run upload).
 * The first pattern that matches the path wins; ":id" matches one segment.
 */
export type HelpPages = [pattern: string, page: string][];

export const tenantHelp: HelpPages = [
  ['/', '/phone/web-phone'],
  ['/calls', '/calls/history'],
  ['/calls/:id', '/calls/call-details'],
  ['/voicemail', '/calls/voicemail'],
  ['/texts', '/calls/texts'],
  ['/texts/:id', '/calls/texts'],
  ['/contacts', '/calls/contacts'],
  ['/contacts/:id', '/calls/contacts'],
  ['/pipeline', '/calls/pipeline'],
  ['/queues', '/manage/queues'],
  ['/queues/live', '/manage/queues-live'],
  ['/callbacks', '/calls/call-back'],
  ['/flows', '/call-flows'],
  ['/flows/:id', '/call-flows/editor'],
  ['/prompts', '/call-flows/prompts'],
  ['/hold-music', '/call-flows/hold-music'],
  ['/opening-hours', '/call-flows/opening-hours'],
  ['/caller-lists', '/call-flows/caller-lists'],
  ['/dashboard', '/insights/dashboard'],
  ['/insights', '/insights/insights'],
  ['/insights/digests/:id', '/insights/insights'],
  ['/people', '/manage/people'],
  ['/regions', '/manage/regions'],
  ['/settings', '/settings'],
  ['/settings/carriers', '/settings/carriers'],
  ['/settings/numbers', '/settings/numbers'],
  ['/settings/branding', '/settings/branding'],
  ['/settings/smarton', '/settings/smarton'],
  ['/audit', '/settings/audit-log'],
  ['/account', '/account'],
];

export const platformHelp: HelpPages = [['*', '/platform']];

/** The languages the guide is published in (apps/docs/pages, pages/lt). */
const GUIDE_LANGUAGES = ['en', 'lt'];

/** The first of these languages the guide is in (by default the one the app shows), else English. */
export function guideLanguage(preferred: readonly string[] = [language]): string {
  return preferred.map((tag) => tag.toLowerCase().split('-')[0]!).find((code) => GUIDE_LANGUAGES.includes(code)) ?? 'en';
}

/**
 * The guide's page for this path, else its start page, in `language`. The
 * docs site has each language under /p/<code>/ (DOCS_URL is the English one).
 */
export function helpUrl(docsUrl: string, pages: HelpPages, path: string, language = 'en'): string {
  const segments = path.replace(/\/+$/, '').split('/');
  const match = pages.find(([pattern]) => {
    if (pattern === '*') return true;
    const parts = pattern.replace(/\/+$/, '').split('/');
    return parts.length === segments.length && parts.every((part, i) => part === ':id' || part === segments[i]);
  });
  return docsUrl.replace(/\/p\/[a-z]{2}\//, `/p/${language}/`) + (match?.[1] ?? '/getting-started');
}
